import { validatePeriodExtras } from "./payPeriodExtrasModel";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { DateTime } from "luxon";
import { commissionBreakdown, salesDay, commissionSplitCount, PAYOUT_TYPES } from "./shootSalesModel";
import { parsePayPeriod } from "./payPeriods";
import { resolveMileage } from "./shootMileage";

const money = cents => new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"}).format(cents/100);
const clean = value => Array.from(String(value ?? ""),character=>character.charCodeAt(0)<32 ? " " : character).join("");

export function commissionReportData(rows, periodValue, defaults = {}, extras = []) {
  const period=parsePayPeriod(periodValue);
  if(!period)throw new Error("Select a standard pay period for the PDF.");
  const matches=rows.filter(row=>parsePayPeriod(row.payPeriod)?.start===period.start);
  const additions=validatePeriodExtras(extras).filter(entry=>entry.payPeriod===period.start);
  if(!matches.length && !additions.length)throw new Error("No shoots or additional payments are assigned to that pay period.");
  const groups=new Map();
  const incomplete=[];
  matches.forEach(row=>{
    const crew=commissionBreakdown(row,defaults);
    if(crew.some(person=>person.totalPayoutCents==null))incomplete.push(row.client || row.id);
    crew.forEach(person=>{
      if(!groups.has(person.nickname))groups.set(person.nickname,[]);
      groups.get(person.nickname).push({row,person});
    });
  });
  if(incomplete.length)throw new Error(`${incomplete.length} shoot(s) in this period need required sales, rates, mileage, or extra-pay reasons. Complete them before exporting. Examples: ${incomplete.slice(0,3).join(", ")}`);
  additions.forEach(entry=>{if(!groups.has(entry.nickname))groups.set(entry.nickname,[]);});
  if(!groups.size)throw new Error("No assigned photographers in this pay period.");
  return {period, photographers:[...groups].sort(([a],[b])=>a.localeCompare(b)).map(([nickname,entries])=>({nickname,
    additions:additions.filter(entry=>entry.nickname===nickname),
    entries:entries.sort((a,b)=>salesDay(a.row.appointmentDate).localeCompare(salesDay(b.row.appointmentDate)) || a.row.client.localeCompare(b.row.client)),
    mileageCents:entries.reduce((sum,entry)=>sum+entry.person.mileageCents,0),
    commissionCents:entries.reduce((sum,entry)=>sum+entry.person.payoutCents,0),
    extraCents:entries.reduce((sum,entry)=>sum+entry.person.extraCents,0)+additions.filter(entry=>entry.nickname===nickname).reduce((sum,entry)=>sum+entry.amountCents,0),
    totalCents:entries.reduce((sum,entry)=>sum+entry.person.totalPayoutCents,0)+additions.filter(entry=>entry.nickname===nickname).reduce((sum,entry)=>sum+entry.amountCents,0),
  }))};
}

export function createCommissionSummaryPDF(rows, periodValue, defaults = {}, extras = []) {
  const {period,photographers}=commissionReportData(rows,periodValue,defaults,extras);
  const pdf=new jsPDF({orientation:"landscape",unit:"pt",format:"letter"});
  const margin=32,width=792,height=612;
  photographers.forEach((photographer,index)=>{
    if(index)pdf.addPage();
    const startPage=pdf.getNumberOfPages();
    const rates=[...new Set(photographer.entries.map(entry=>entry.person.commissionRate))];
    const types=[...new Set(photographer.entries.map(entry=>entry.person.payoutType))];
    const header=()=>{
      pdf.setTextColor(20);pdf.setFont("helvetica","bold");pdf.setFontSize(15);
      pdf.text("Commission Summary",margin,48);
      pdf.setFontSize(11);pdf.text(clean(photographer.nickname),margin,68);
      pdf.setFont("helvetica","normal");pdf.setFontSize(9);
      pdf.text(`Check Date: ${period.checkLabel}`,margin,86);
      pdf.text(`Pay Period: ${period.label}`,margin,101);
      pdf.text(`Payout type: ${!types.length ? "Additional pay only" : types.length===1 ? clean(PAYOUT_TYPES[types[0]]) : "Varies by shoot"} | Rate: ${rates.length===1 && rates[0]!=null ? rates[0]+"%" : "See rows"}`,margin,116);
      pdf.setFontSize(8);pdf.setTextColor(75);
      pdf.setDrawColor(35);pdf.setLineWidth(.8);pdf.rect(24,24,width-48,height-48);
    };
    pdf.autoTable({
      startY:156,margin:{top:156,left:margin,right:margin,bottom:44},
      theme:"plain",showHead:"everyPage",showFoot:"lastPage",rowPageBreak:"avoid",
      styles:{font:"helvetica",fontSize:8,cellPadding:{top:6,right:6,bottom:6,left:6},overflow:"linebreak",textColor:20,valign:"middle"},
      headStyles:{fontStyle:"bold",fillColor:[236,240,244],lineColor:[80,80,80],lineWidth:{bottom:.7}},
      bodyStyles:{lineColor:[220,220,220],lineWidth:{bottom:.25}},
      footStyles:{fontStyle:"bold",fillColor:[236,240,244],lineWidth:{top:.8},lineColor:[60,60,60]},
      columnStyles:{0:{cellWidth:60},1:{cellWidth:48},2:{cellWidth:201},3:{cellWidth:72},4:{cellWidth:36},5:{cellWidth:40},6:{cellWidth:65},7:{cellWidth:71},8:{cellWidth:64},9:{cellWidth:71}},
      // AutoTable applies columnStyles to the body, but not header/footer cells.
      // Set alignment after all section styles so headings, values and totals agree.
      didParseCell:({column,cell})=>{
        cell.styles.halign=column.index>=3 ? "right" : "left";
      },
      head:[["Date","School\nCode","School / Client","Sales\nShare","Rate","Miles","Mileage\nPay","Commission","Extras","Total"]],
      body:[...photographer.entries.map(({row,person})=>[
        DateTime.fromISO(salesDay(row.appointmentDate)).toFormat("MM/dd/yyyy"),
        clean(resolveMileage(row).clientID),`${clean(row.client)}${person.excludeFromCommission===true ? "\nExcluded from commission split" : ""}${types.length>1 ? `\n${clean(PAYOUT_TYPES[person.payoutType])}` : ""}${person.extraCents ? `\nExtra: ${clean(person.extraReason)}` : ""}`,
        person.excludeFromCommission===true || row.salesCents==null || !commissionSplitCount(row) ? "-" : money(row.salesCents/commissionSplitCount(row)),person.excludeFromCommission===true || person.payoutType==="salary" ? "-" : person.commissionRate==null ? "-" : `${person.commissionRate}%`,resolveMileage(row).miles==null ? "-" : String(resolveMileage(row).miles),
        money(person.mileageCents),money(person.payoutCents),money(person.extraCents),money(person.totalPayoutCents),
      ]),...photographer.additions.map(entry=>["","",`Additional pay: ${clean(entry.label)}${entry.notes ? "\n"+clean(entry.notes) : ""}`,"","","","","",money(entry.amountCents),money(entry.amountCents)])],
      foot:[["Total","",`${photographer.entries.length} ${photographer.entries.length===1 ? "shoot" : "shoots"}`,"","","",money(photographer.mileageCents),money(photographer.commissionCents),money(photographer.extraCents),money(photographer.totalCents)]],
      didDrawPage:header,
    });
    const endPage=pdf.getNumberOfPages();
    for(let page=startPage;page<=endPage;page++){
      pdf.setPage(page);pdf.setFont("helvetica","normal");pdf.setFontSize(8);pdf.setTextColor(90);
      pdf.text(`${clean(photographer.nickname)} | ${page-startPage+1} of ${endPage-startPage+1}`,width-margin,height-30,{align:"right"});
    }
  });
  return pdf;
}

export function exportCommissionSummaryPDF(rows, periodValue, defaults = {}, extras = []) {
  const period=parsePayPeriod(periodValue);
  const pdf=createCommissionSummaryPDF(rows,periodValue,defaults,extras);
  pdf.save(`${period.checkDate}-Commission-Summary.pdf`);
}

export function payPeriodPayoutCSV(rows,periodValue,defaults={},extras=[]) {
  const {period,photographers}=commissionReportData(rows,periodValue,defaults,extras);
  const records=[["Pay period","Check date","Photographer","Entry type","Client / label","Notes","Commission","Mileage","Extras","Total"]];
  photographers.forEach(person=>{
    person.entries.forEach(({row,person:crew})=>records.push([period.label,period.checkLabel,person.nickname,"Shoot",row.client,[crew.excludeFromCommission===true ? "Excluded from commission split" : "",crew.extraReason || ""].filter(Boolean).join("; "),crew.payoutCents/100,crew.mileageCents/100,crew.extraCents/100,crew.totalPayoutCents/100]));
    person.additions.forEach(entry=>records.push([period.label,period.checkLabel,person.nickname,"Additional pay",entry.label,entry.notes,0,0,entry.amountCents/100,entry.amountCents/100]));
    records.push([period.label,period.checkLabel,person.nickname,"TOTAL","","",person.commissionCents/100,person.mileageCents/100,person.extraCents/100,person.totalCents/100]);
  });
  return records.map(row=>row.map((value,index)=>'"'+(index>=6 && typeof value==="number" ? value.toFixed(2) : String(value)).replace(/"/g,'""')+'"').join(",")).join("\r\n");
}
